import React, { useState } from 'react';
import {
  Facebook, Instagram, Loader2, Link2, RefreshCw, PlugZap, ShieldCheck,
  AlertTriangle, CheckCircle2, XCircle, Copy, Unplug,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const STATUS_UI = {
  connected: { label: 'Conectado', cls: 'bg-green-100 text-green-700', Icon: ShieldCheck },
  reconnect_required: { label: 'Meta necesita reconexión', cls: 'bg-red-100 text-red-700', Icon: AlertTriangle },
  select_page: { label: 'Elige tu página', cls: 'bg-amber-100 text-amber-700', Icon: AlertTriangle },
  error: { label: 'Error de verificación', cls: 'bg-red-100 text-red-700', Icon: XCircle },
  not_connected: { label: 'No conectado', cls: 'bg-gray-100 text-gray-600', Icon: PlugZap },
};

function fmtDate(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleString('es-CO'); } catch { return String(v); }
}

export default function MetaConnect({ settings, onReload }) {
  const { toast } = useToast();
  const m = settings?.meta || {};
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);
  const [pages, setPages] = useState(null);
  const [loadingPages, setLoadingPages] = useState(false);

  const status = m.connection_status || 'not_connected';
  const su = STATUS_UI[status] || STATUS_UI.not_connected;

  const startOAuth = async () => {
    if (!m.oauth_ready) {
      toast({ title: 'Falta configuración', description: 'Guarda primero el Meta App ID y App Secret.', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const { url } = await api.metaAuthorizeUrl();
      const popup = window.open(url, 'meta_oauth', 'width=600,height=760');
      const timer = setInterval(async () => {
        if (popup && popup.closed) {
          clearInterval(timer);
          setBusy(false);
          await onReload?.();
          toast({ title: 'Conexión con Meta actualizada', description: 'Revisa el estado abajo.' });
        }
      }, 1500);
    } catch (e) {
      setBusy(false);
      toast({ title: 'No se pudo iniciar OAuth', description: String(e.message || e), variant: 'destructive' });
    }
  };

  const runTest = async () => {
    setBusy(true);
    try {
      const r = await api.metaTest();
      setTest(r);
      await onReload?.();
    } catch (e) {
      toast({ title: 'Error al probar', description: String(e.message || e), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const loadPages = async () => {
    setLoadingPages(true);
    try {
      const r = await api.metaPages();
      setPages(r.pages || []);
    } catch (e) {
      toast({ title: 'No se pudieron listar las páginas', description: String(e.message || e), variant: 'destructive' });
    } finally { setLoadingPages(false); }
  };

  const choosePage = async (pageId) => {
    setBusy(true);
    try {
      await api.metaSelectPage({ page_id: pageId });
      setPages(null);
      await onReload?.();
      toast({ title: 'Página conectada' });
    } catch (e) {
      toast({ title: 'Error al seleccionar la página', description: String(e.message || e), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('¿Desconectar Meta? Se borran los tokens de página/usuario (App ID/Secret se conservan).')) return;
    setBusy(true);
    try {
      await api.metaDisconnect();
      setTest(null);
      await onReload?.();
      toast({ title: 'Meta desconectado' });
    } catch (e) {
      toast({ title: 'Error al desconectar', description: String(e.message || e), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const copyRedirect = () => {
    if (m.redirect_uri) {
      navigator.clipboard?.writeText(m.redirect_uri);
      toast({ title: 'Redirect URI copiado' });
    }
  };

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 space-y-4" data-testid="meta-connect-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Facebook className="w-5 h-5 text-blue-600" />
          <Instagram className="w-5 h-5 text-pink-600" />
          <h3 className="font-bold text-habita-dark">Conexión con Meta (OAuth)</h3>
        </div>
        <span className={`text-xs px-2.5 py-1 rounded-full font-bold flex items-center gap-1.5 ${su.cls}`} data-testid="meta-status-badge">
          <su.Icon className="w-3.5 h-3.5" /> {su.label}
        </span>
      </div>

      <p className="text-sm text-gray-500">
        Conecta tu página de Facebook e Instagram con el flujo oficial de Meta. Sin pegar tokens
        manualmente: Habita obtiene y renueva el Page Access Token de larga duración por ti.
      </p>

      {/* Banner de reconexión */}
      {status === 'reconnect_required' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2" data-testid="meta-reconnect-banner">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm text-red-700">
            <b>Meta necesita reconexión.</b> El token dejó de ser válido (sesión expirada o permisos revocados, código 190). Pulsa <b>Reconectar Meta</b>.
          </div>
        </div>
      )}

      {/* Estado de la conexión */}
      {(status === 'connected' || m.page_name) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs bg-habita-cream/40 rounded-xl p-3" data-testid="meta-connection-info">
          <div><span className="text-gray-500">Página: </span><b className="text-habita-dark">{m.page_name || '—'}</b></div>
          <div><span className="text-gray-500">Instagram: </span><b className="text-habita-dark">{m.ig_username ? `@${m.ig_username}` : (m.ig_user_id_set ? 'conectado' : '—')}</b></div>
          <div><span className="text-gray-500">Conectado: </span>{fmtDate(m.connected_at)}</div>
          <div><span className="text-gray-500">Última verificación: </span>{fmtDate(m.last_checked_at)}</div>
          <div><span className="text-gray-500">Token expira: </span>{m.token_expires_at ? fmtDate(m.token_expires_at) : 'larga duración'}</div>
          <div><span className="text-gray-500">Acceso a datos expira: </span>{m.data_access_expires_at ? fmtDate(m.data_access_expires_at) : '—'}</div>
        </div>
      )}

      {/* Selección de página (cuando es ambiguo) */}
      {status === 'select_page' && (
        <div className="border border-amber-200 rounded-xl p-3" data-testid="meta-select-page">
          <p className="text-sm text-amber-700 font-semibold mb-2">Autorizaste Meta. Elige la página a conectar:</p>
          {!pages ? (
            <button onClick={loadPages} disabled={loadingPages} data-testid="meta-load-pages-btn" className="px-3 h-9 rounded-lg border border-gray-300 text-sm font-semibold flex items-center gap-1.5">
              {loadingPages ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Ver mis páginas
            </button>
          ) : (
            <div className="space-y-1.5">
              {pages.map((p) => (
                <div key={p.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2">
                  <span className="text-sm text-habita-dark">{p.name} {p.has_instagram && <span className="text-[10px] text-pink-600">· IG</span>}</span>
                  <button onClick={() => choosePage(p.id)} disabled={busy} data-testid={`meta-choose-page-${p.id}`} className="px-2.5 h-8 rounded-lg bg-habita text-white text-xs font-semibold">Conectar</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Resultado de "Probar conexión" */}
      {test && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="meta-test-result">
          <div className={`rounded-xl p-3 border flex items-center gap-2 ${test.facebook?.connected ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
            {test.facebook?.connected ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
            <div className="text-sm"><b>Facebook</b> {test.facebook?.connected ? `· ${test.facebook.page_name || 'conectado'}` : 'no conectado'}</div>
          </div>
          <div className={`rounded-xl p-3 border flex items-center gap-2 ${test.instagram?.connected ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
            {test.instagram?.connected ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
            <div className="text-sm"><b>Instagram</b> {test.instagram?.connected ? `· @${test.instagram.username || ''}` : 'no conectado'}</div>
          </div>
          <div className="sm:col-span-2 text-xs text-gray-500">
            Estado del token: <b className="text-habita-dark">{test.token_status}</b> · Última verificación: {fmtDate(test.last_checked)}
          </div>
        </div>
      )}

      {/* Acciones */}
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="meta-connect-btn" onClick={startOAuth} disabled={busy || !m.oauth_ready}
          className="px-4 h-10 rounded-xl bg-habita text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />}
          {m.oauth_connected || status === 'connected' ? 'Reconectar Meta' : 'Conectar Meta'}
        </button>
        <button data-testid="meta-test-btn" onClick={runTest} disabled={busy || !m.page_access_token_set}
          className="px-4 h-10 rounded-xl border border-gray-300 font-semibold text-sm flex items-center gap-2 hover:bg-habita-cream disabled:opacity-50">
          <RefreshCw className="w-4 h-4" /> Probar conexión Meta
        </button>
        {(m.oauth_connected || m.page_access_token_set) && (
          <button data-testid="meta-disconnect-btn" onClick={disconnect} disabled={busy}
            className="px-4 h-10 rounded-xl border border-rose-200 text-rose-600 font-semibold text-sm flex items-center gap-2 hover:bg-rose-50 disabled:opacity-50">
            <Unplug className="w-4 h-4" /> Desconectar
          </button>
        )}
      </div>

      {!m.oauth_ready && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2">
          Para usar OAuth, guarda primero tu <b>Meta App ID</b> y <b>App Secret</b> abajo (sección de credenciales).
        </p>
      )}

      {/* Redirect URI a registrar */}
      <div className="border-t border-gray-100 pt-3">
        <p className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> Redirect URI que debes registrar en Meta Developers (Facebook Login → Settings → Valid OAuth Redirect URIs):</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 break-all" data-testid="meta-redirect-uri">{m.redirect_uri || '—'}</code>
          <button onClick={copyRedirect} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50" title="Copiar"><Copy className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}
